import { useMemo } from 'react';
import { scaleOrdinal } from 'd3-scale';
import { ThreeDBarConfig, ThreeDBarDatum, ThreeDBarDataMapping } from '../config/threeDBarTypes';

export const useThreeDBarData = (
    data: any[],
    mapping: ThreeDBarDataMapping,
    config: ThreeDBarConfig,
    palette?: string[]
) => {
    const { processedData, xCategories, zCategories, seriesNames } = useMemo(() => {
        if (!data || data.length === 0) return { processedData: [], xCategories: [], zCategories: [], seriesNames: [] };

        const xSet = new Set<string>();
        const zSet = new Set<string>();
        const seriesSet = new Set<string>();

        const colorScale = palette && palette.length > 0 ? scaleOrdinal(palette) : null;

        const processed = data.map((item, i) => {
            const xVal = String(item[mapping.xField] || '');
            const zVal = mapping.zField ? String(item[mapping.zField]) : '';
            const val = parseFloat(item[mapping.valueField]);
            const series = mapping.seriesField ? String(item[mapping.seriesField]) : 'Series 1';
            const colorVal = mapping.colorField ? item[mapping.colorField] : series;

            if (xVal) xSet.add(xVal);
            if (zVal) zSet.add(zVal);
            seriesSet.add(series);

            if (isNaN(val)) return null;

            return {
                id: `bar-${i}`,
                xCategory: xVal,
                zCategory: zVal,
                value: val,
                series: series,
                color: colorScale ? colorScale(String(colorVal)) : (item[mapping.colorField] || config.singleColor),
                originalData: item
            } as ThreeDBarDatum;
        }).filter(Boolean) as ThreeDBarDatum[];

        return {
            processedData: processed,
            xCategories: Array.from(xSet),
            zCategories: Array.from(zSet),
            seriesNames: Array.from(seriesSet)
        };
    }, [data, mapping, config, palette]);

    const yScale = useMemo(() => {
        const maxValue = Math.max(...processedData.map(d => d.value), 0);
        return {
            max: maxValue,
            type: config.yScaleType
        };
    }, [processedData, config.yScaleType]);

    return { processedData, xCategories, zCategories, seriesNames, yScale };
};
